import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, Calendar as CalendarIcon, Scissors } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Calendar } from '@/app/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useRopeCutTrendsReport } from '@/hooks/use-reports';
import { cn } from '@/app/components/ui/utils';
import { formatDateOnly } from '@/lib/date-utils';

export function RopeCutTrendsPage() {
  const [startDate, setStartDate] = useState<Date | undefined>(subDays(new Date(), 180));
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [isExporting, setIsExporting] = useState(false);

  const filters = {
    start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
  };

  const { data, isLoading, error } = useRopeCutTrendsReport(filters);

  const chartData = data?.trends?.map(trend => ({
    name: `${trend.month}/${trend.year}`,
    cuts: trend.count,
    totalLength: parseFloat(trend.total_cut_length.toFixed(2)),
  })) || [];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const now = new Date();
      const csvContent = [
        ['Rope Cut Trends Report'],
        [`Date Range: ${filters.start_date || 'All'} to ${filters.end_date || 'All'}`],
        [],
        ['Summary'],
        ['Total Cuts', data?.total_cuts?.toString() || '0'],
        ['Total Length Removed (m)', data?.total_length_removed?.toFixed(2) || '0'],
        [],
        ['Monthly Trends'],
        ['Month/Year', 'Number of Cuts', 'Total Length (m)'],
        ...(data?.trends?.map(trend => [
          `${trend.month}/${trend.year}`,
          trend.count.toString(),
          trend.total_cut_length.toFixed(2)
        ]) || []),
        [],
        ['Detailed Cuts'],
        ['Date', 'Asset', 'Serial Number', 'Cut Length (m)', 'Remaining Length (m)', 'Performed By'],
        ...(data?.cuts?.map(cut => [
          formatDateOnly(cut.date),
          cut.asset_name,
          cut.serial_number,
          cut.cut_length.toFixed(2),
          cut.remaining_length.toFixed(2),
          cut.performed_by
        ]) || [])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rope-cut-trends-${now.toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load rope cut trends report. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Rope Cut Trends</h1>
          <p className="text-gray-600 mt-1">Analyze rope cutting patterns and equipment lifespan</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={isExporting || !data}>
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Date Range</CardTitle>
          <CardDescription>Select the period for trend analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cuts</CardTitle>
            <Scissors className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.total_cuts || 0}</div>
            <p className="text-xs text-muted-foreground">In selected period</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Length Removed</CardTitle>
            <Scissors className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.total_length_removed?.toFixed(2) || 0} m</div>
            <p className="text-xs text-muted-foreground">Rope removed in period</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rope Cuts Over Time</CardTitle>
          <CardDescription>Monthly trend of rope cuts and total length removed</CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No rope cut data available for the selected period</p>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="cuts"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="Number of Cuts"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="totalLength"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Total Length (m)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rope Cut Details</CardTitle>
          <CardDescription>Individual rope cuts in the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          {!data?.cuts || data.cuts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No rope cuts recorded in the selected period</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Serial Number</TableHead>
                  <TableHead className="text-right">Cut Length (m)</TableHead>
                  <TableHead className="text-right">Remaining (m)</TableHead>
                  <TableHead>Performed By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.cuts.map((cut) => (
                  <TableRow key={cut.id}>
                    <TableCell>{formatDateOnly(cut.date)}</TableCell>
                    <TableCell>
                      <Link
                        to={`/assets/${cut.asset_id}`}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        {cut.asset_name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-600">{cut.serial_number}</TableCell>
                    <TableCell className="text-right font-medium">{cut.cut_length.toFixed(2)}</TableCell>
                    <TableCell className="text-right">{cut.remaining_length.toFixed(2)}</TableCell>
                    <TableCell>{cut.performed_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
